import apiClient from '../lib/api';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string | null;
  emailConfirmed: boolean;
  roles: string[];
  permissions: string[];
}

export interface AuthTokenResponse {
  accessToken: string;
  expiresAtUtc: string;
  user: CurrentUser;
  // refreshToken intentionally absent: BE delivers it only via the HttpOnly
  // cookie, so it stays out of reach of any FE-loaded script (XSS resistance).
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

export const login = async (email: string, password: string): Promise<AuthTokenResponse> => {
  const { data } = await apiClient.post<AuthTokenResponse>('/api/v1/auth/login', { email, password });
  return data;
};

export const register = async (
  email: string, password: string, displayName: string,
): Promise<{ id: string; email: string }> => {
  const { data } = await apiClient.post('/api/v1/auth/register', { email, password, displayName });
  return data;
};

/**
 * Exchanges the HttpOnly refresh-token cookie for a new access token + rotated
 * refresh token. Browser clients don't pass anything in the body — the cookie
 * carries the token. Used by api.ts on 401, by AuthCallback after OAuth, and
 * by the boot-time session restore in AuthContext.
 */
export const refresh = async (): Promise<AuthTokenResponse> => {
  const { data } = await apiClient.post<AuthTokenResponse>('/api/v1/auth/refresh', {});
  return data;
};

export const logout = async (): Promise<void> => {
  await apiClient.post('/api/v1/auth/logout', {}).catch(() => {
    // 401 here means the token is already gone — proceed with local cleanup.
  });
};

export const me = async (): Promise<CurrentUser> => {
  const { data } = await apiClient.get<CurrentUser>('/api/v1/auth/me');
  return data;
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  await apiClient.post('/api/v1/auth/me/change-password', { currentPassword, newPassword });
};

export const forgotPassword = async (email: string): Promise<void> => {
  await apiClient.post('/api/v1/auth/forgot-password', { email });
};

export const resetPassword = async (email: string, token: string, newPassword: string): Promise<void> => {
  await apiClient.post('/api/v1/auth/reset-password', { email, token, newPassword });
};

export const confirmEmail = async (email: string, token: string): Promise<void> => {
  await apiClient.post('/api/v1/auth/confirm-email', { email, token });
};

export const resendConfirmEmail = async (email: string): Promise<void> => {
  await apiClient.post('/api/v1/auth/resend-confirm-email', { email });
};

/**
 * Builds the absolute URL for an OAuth provider redirect. FE points
 * window.location.href at this URL — the BE handles the dance and redirects
 * back to the configured FE callback URL with ?session=ok.
 */
export const getExternalLoginUrl = (provider: 'google' | 'facebook'): string => {
  const base = process.env.REACT_APP_API_URL || '';
  return `${base}/api/v1/auth/external/${provider}`;
};
